import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Typography, Stack, FormControlLabel, Radio, Grid, RadioGroup, Box } from '@mui/material'
import { TextField } from '@e3dc-react/shell/Elements/InputFields'
import { makeStyles } from 'tss-react/mui'
import { useFormikContext } from 'formik'
import { ChargeCardStatus } from 'src/Libs/e-mobility/charging-sessions'
import { EditChargingCardFormData } from '../models/e-mobility-charging-cards-models'

const useStyles = makeStyles()(theme => ({
  vehicleInputWrapper: {
    backgroundColor: theme.palette.shades.shade04,
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
  },
  title: {
    fontWeight: 500,
  },
  inputs: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
}))

interface EditChargingCardFormProps {
  status: ChargeCardStatus
}

export const EditChargingCardForm: React.FC<EditChargingCardFormProps> = ({ status }): JSX.Element => {
  const { handleSubmit, handleChange, setFieldValue, values, errors } = useFormikContext<EditChargingCardFormData>()
  const { classes } = useStyles()
  const { t } = useCustomTranslation()

  return (
    <form onSubmit={handleSubmit}>
      <Typography variant="body2" component="p">
        {t('emobility.chargingCards.status.status')}
      </Typography>
      <Stack direction="column" spacing={4}>
        <RadioGroup aria-label="charging-card-status" defaultValue={status} onChange={handleChange}>
          <FormControlLabel name="status" value="OK" control={<Radio color="primary" />} label={t('emobility.chargingCards.status.OK')} />
          <FormControlLabel name="status" value="NO_AUTH" control={<Radio color="primary" />} label={t('emobility.chargingCards.status.NO_AUTH')} />
          <FormControlLabel name="status" value="DENIED" control={<Radio color="primary" />} label={t('emobility.chargingCards.status.DENIED')} />
        </RadioGroup>

        <TextField
          value={values.chargingCardName}
          onChange={value => setFieldValue('chargingCardName', value)}
          label={t('emobility.chargingCards.chargingCard')}
          placeholder={t('emobility.chargingCards.chargingCard')}
          name="chargingCardName"
          clearable={false}
        />
        <TextField
          value={values.chargingCardDescription}
          onChange={value => setFieldValue('chargingCardDescription', value)}
          label={t('emobility.chargingCards.edit.description')}
          placeholder={t('emobility.chargingCards.edit.chargingCardDescription')}
          name="chargingCardDescription"
          clearable={false}
        />

        <Box className={classes.vehicleInputWrapper}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="body2" component="p" className={classes.title}>
                {t('emobility.chargingCards.edit.vehicleInformation')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                value={values.vehicleName}
                onChange={value => setFieldValue('vehicleName', value)}
                label={t('emobility.chargingCards.vehicle')}
                placeholder={t('emobility.chargingCards.edit.vehicleName')}
                name="vehicleName"
                clearable={false}
                className={classes.inputs}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                value={values.licensePlate}
                onChange={value => setFieldValue('licensePlate', value)}
                label={t('emobility.chargingCards.edit.licensePlateLabel')}
                placeholder={t('emobility.chargingCards.edit.licensePlate')}
                name="licensePlate"
                clearable={false}
                className={classes.inputs}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                value={values.vehicleDescription}
                onChange={value => setFieldValue('vehicleDescription', value)}
                label={t('emobility.chargingCards.edit.description')}
                placeholder={t('emobility.chargingCards.edit.vehicleDescription')}
                name="vehicleDescription"
                clearable={false}
                className={classes.inputs}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                type="number"
                step={0.1}
                value={values.avgConsumption}
                onChange={value => setFieldValue('avgConsumption', value)}
                label={t('emobility.chargingCards.edit.consumption')}
                placeholder={t('emobility.chargingCards.edit.consumptionPerKm')}
                name="avgConsumption"
                clearable={false}
                className={classes.inputs}
                helperText={errors.avgConsumption}
                useTooltipErrorMessage={false}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="number"
                step={0.1}
                value={values.batteryCapacity}
                onChange={value => setFieldValue('batteryCapacity', value)}
                label={t('emobility.chargingCards.edit.capacity')}
                placeholder={t('emobility.chargingCards.edit.capacityKwh')}
                name="batteryCapacity"
                clearable={false}
                className={classes.inputs}
                helperText={errors.batteryCapacity}
                useTooltipErrorMessage={false}
              />
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </form>
  )
}
